/* eslint-disable camelcase */
import * as React from 'react'
import { Helmet } from 'react-helmet'
import { helmetJsonLdProp } from 'react-schemaorg'
import config from '../../../config'
import { useIntl } from 'react-intl'
import getCurrentUrl from '../../../helpers/getCurrentUrl'
import getHostUrl from '../../../helpers/getHostUrl'
import Product from 'api/models/Product'
import Store from 'api/models/Store'

interface RichResult {
  type: string
  product: Product
  productPath: string
  store: Store
  storePath: string
}

interface Props {
  title?: string
  description?: string
  keywords?: string
  theme_color?: string
  msapplication_navbutton_color?: string
  apple_mobile_web_app_status_bar_style?: string
  og_locale?: string
  og_title?: string
  og_description?: string
  og_type?: string
  og_url?: string
  og_image?: string
  og_image_secure_url?: string
  og_image_type?: string
  og_image_width?: string
  og_image_height?: string
  og_image_alt?: string
  og_site_name?: string
  fb_app_id?: string
  google_site_verification?: string
  product_price_amount?: string
  product_price_currency?: string
  defer?: boolean
  richResult?: RichResult
  noIndex?: boolean
}

const StoreMetatags = ({
  title = '',
  description = '',
  keywords = '',
  theme_color = '',
  msapplication_navbutton_color = '',
  apple_mobile_web_app_status_bar_style = '',
  og_locale = '',
  og_title = '',
  og_description = '',
  og_type = '',
  og_url = '',
  og_image = '',
  og_image_secure_url = '',
  og_image_type = '',
  og_image_width = '',
  og_image_height = '',
  og_image_alt = '',
  og_site_name = '',
  fb_app_id = '',
  google_site_verification = '',
  product_price_amount = '',
  product_price_currency = '',
  defer = true,
  richResult,
  noIndex = false
}: Props) => {
  const intl = useIntl()

  const getStoreMeta = () => {
    return helmetJsonLdProp({
      '@context': 'https://schema.org/',
      '@type': 'Organization',
      name: richResult?.store.getName(),
      description: JSON.stringify(richResult?.store.getAboutText() || richResult?.store.getDescription() || ''),
      image: richResult?.store.getOwnerPicture(),
      url: getCurrentUrl(),
      sameAS: [
        richResult?.store
          .getOwnerSocial()
          .map(social => '"' + social.url + '"')
          .join()
      ]
    })
  }

  const getProductsMeta = () => {
    const productsListMeta = richResult?.store.getProducts().map((product: Product) => ({
      '@type': 'Product',
      name: product.getName(),
      image: product.getImage(),
      description: product.getDescription(),
      productID: product.getId(),
      url: `https://${
        window.location.host
      }/store/${richResult?.store.getShortId()}/product/${product.getId()}?modifier=auto_add`,

      offers: {
        '@type': 'Offer',
        priceCurrency: 'USD',
        price: product.getPrice(),
        availability: `https://schema.org/${product.getAvailability()}`,
        inventoryLevel: {
          value: product.isLimited() ? product.getStock() : 'unlimited'
        }
      }
    }))

    return helmetJsonLdProp({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      url: window.location.origin,
      numberOfItems: richResult?.store.getProducts().length,
      itemListElement: productsListMeta
    })
  }

  const getBreadcrumbMetaForStore = () => {
    return helmetJsonLdProp({
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Hausmart', item: getHostUrl() },
        {
          '@type': 'ListItem',
          position: 2,
          name: richResult?.store.getName(),
          item: `${getHostUrl()}${richResult?.storePath}`
        }
      ]
    })
  }

  return (
    <>
      {richResult && <Helmet script={[getStoreMeta(), getProductsMeta(), getBreadcrumbMetaForStore()]} />}
      <Helmet defer={defer}>
        <title>{title}</title>
        <meta name="description" content={description || intl.formatMessage({ id: 'metatags.description' })} />
        <meta name="keywords" content={keywords || intl.formatMessage({ id: 'metatags.keywords' })} />
        <meta name="theme-color" content={theme_color || '#222'} />
        <meta name="msapplication-navbutton-color" content={msapplication_navbutton_color || '#222'} />
        <meta name="apple-mobile-web-app-status-bar-style" content={apple_mobile_web_app_status_bar_style || '#222'} />
        <meta property="og:locale" content={og_locale || 'en_US'} />
        <meta property="og:type" content={og_type || 'website'} />
        <meta property="og:url" content={og_url || getCurrentUrl()} />
        <meta property="og:title" content={og_title || intl.formatMessage({ id: 'metatags.title' })} />
        <meta
          property="og:description"
          content={og_description || intl.formatMessage({ id: 'metatags.description' })}
        />
        <meta property="og:site_name" content={og_site_name || 'HausMart'} />
        <meta property="og:image" content={og_image || 'https://hausmart.com/images/hmsharing_new_v2.png'} />
        <meta
          property="og:image:secure_url"
          content={og_image_secure_url || 'https://hausmart.com/images/hmsharing_new_v2.png'}
        />
        <meta property="og:image:type" content={og_image_type || 'image/png'} />
        <meta property="og:image:width" content={og_image_width || '1200'} />
        <meta property="og:image:height" content={og_image_height || '630'} />
        <meta property="og:image:alt" content={og_image_alt || intl.formatMessage({ id: 'metatags.image.alt' })} />
        <meta property="fb:app_id" content={fb_app_id || '153212061969401'} />
        <meta property="product:price:amount" content={product_price_amount} />
        <meta property="product:price:currency" content={product_price_currency} />
        <meta name="google-site-verification" content={google_site_verification || config.googleSiteVerification} />
        <meta name="robots" content={noIndex ? 'noindex' : 'index'} />
      </Helmet>
    </>
  )
}

export { StoreMetatags }
